import type { IGSetupGuideUrlRedirectStatuses, ISetupGuideCampaignUrlRedirect } from '../types';
import type { IGSetupGuideItem } from '~/presentation/common';

import { useShopMetaCreateOrUpdateMutation } from '~/api/graphql/mutations/shopMetaCreateOrUpdate.generated';
import {
  mergeSetupGuideStatuses,
  useGSetupGuideContext,
  useWindowSize,
} from '~/presentation/common';
import { GEMX_URL_REDIRECT_SETUP_GUIDE_KEY } from '~/presentation/common/components/GSetupGuide/constants';
import { useSetupGuideState } from '~/presentation/common/components/GSetupGuide/useSetupGuideState';
import { useUrlRedirectSetupGuideFirstVisit } from './useUrlRedirectSetupGuideFirstVisit';
import { useShopMetaByKeyQuery } from '~/api/graphql/queries/shopMetaByKey.generated';
import { EBaseSetupGuideCampaign, IVariantType } from '~/modules/campaign/types';
import { findVariantByType, useCampaignContext } from '~/modules/campaign';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ESetupGuideCampaignUrlRedirect } from '../types';
import { captureSentryError } from '~/exceptions/sentry';
import { useQueryClient } from '@tanstack/react-query';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { hasVariantUrlDestination } from '../utils';
import { useTranslation } from 'react-i18next';
import { useEffectRunOnce } from '~/hooks';
import { parseShopMeta } from '~/utils';

export interface ICampaignUrlRedirectSetupGuide {
  setupGuideState: boolean;
  setupGuideSteps: IGSetupGuideItem<ISetupGuideCampaignUrlRedirect>[];
  handleShowSetupGuide: (shouldCheck?: boolean) => void;
  handleUpdateSetupGuide: (setupGuideKeys: ISetupGuideCampaignUrlRedirect[]) => void;
}

export const useCampaignUrlRedirectSetupGuide = (): ICampaignUrlRedirectSetupGuide => {
  const { setupGuideState, setShowSetupGuide } = useSetupGuideState();

  const { t } = useTranslation();
  const { windowWidth } = useWindowSize();
  const { campaign } = useCampaignContext();
  const { expandedId, setExpandedId } = useGSetupGuideContext();

  const queryClient = useQueryClient();

  const { data: setupGuideStatuses, isFetched: isFetchedSetupGuideMetaData } =
    useShopMetaByKeyQuery<IGSetupGuideUrlRedirectStatuses>(
      { key: GEMX_URL_REDIRECT_SETUP_GUIDE_KEY },
      { select: parseShopMeta },
    );
  const shopMetaCreateOrUpdateMutation = useShopMetaCreateOrUpdateMutation();

  useUrlRedirectSetupGuideFirstVisit({ isReady: isFetchedSetupGuideMetaData });

  const isCampaignRunning = campaign?.status === CAMPAIGN_STATUS.RUNNING;
  const isSelectedControl = hasVariantUrlDestination(
    findVariantByType(campaign, IVariantType.VARIANT_A)?.url,
  );
  const isSelectedVariant = hasVariantUrlDestination(
    findVariantByType(campaign, IVariantType.VARIANT_B)?.url,
  );

  const setupGuideSteps: IGSetupGuideItem<ISetupGuideCampaignUrlRedirect>[] = useMemo(() => {
    const isCompletedControlPage =
      setupGuideStatuses?.[ESetupGuideCampaignUrlRedirect.SET_CONTROL_URL] ?? false;
    const isCompletedVariantPage =
      setupGuideStatuses?.[ESetupGuideCampaignUrlRedirect.SET_VARIANT_URL] ?? false;

    return [
      {
        id: ESetupGuideCampaignUrlRedirect.SET_CONTROL_URL,
        title: t('Set the Control URL'),
        description: t(
          "This is the page you're already running. Use this URL in your ads and links, some visitors stay here and the rest are redirected to the Variant.",
        ),
        completed: isCompletedControlPage,
        isHiddenAction: isCompletedControlPage && isSelectedControl,
        isMarkDoneByAction: true,
      },
      {
        id: ESetupGuideCampaignUrlRedirect.SET_VARIANT_URL,
        title: t('Set the Variant URL'),
        description: t(
          'This is where redirected visitors land. Choose any published URL in your store. You need to select the Control first.',
        ),
        completed: isCompletedVariantPage,
        isHiddenAction: isCompletedVariantPage && isSelectedVariant,
        disabledReason: !isSelectedControl
          ? t('Please set the Control URL to proceed.')
          : undefined,
        isMarkDoneByAction: true,
      },
      {
        id: EBaseSetupGuideCampaign.SETUP_TRAFFIC_SPLIT,
        title: t('Set traffic split'),
        description: t('Decide what percentage of visitors will see the Control vs. the Variant.'),
        completed: setupGuideStatuses?.[EBaseSetupGuideCampaign.SETUP_TRAFFIC_SPLIT] ?? false,
      },
      {
        id: EBaseSetupGuideCampaign.SET_WINNING_METRIC,
        title: t('Set winning metric'),
        description: t(
          'Choose the key metric you prioritize. The system will use it to determine the winning version.',
        ),
        completed: setupGuideStatuses?.[EBaseSetupGuideCampaign.SET_WINNING_METRIC] ?? false,
      },
      {
        id: EBaseSetupGuideCampaign.SET_UP_DEVICE_VISITOR_TRAFFIC_SOURCE_MARKET,
        title: t('Set up device, visitor & traffic source'),
        description: t(
          'Include only visitors from selected devices, visitor types (All, New, Returning), traffic sources, and target markets.',
        ),
        completed:
          setupGuideStatuses?.[
            EBaseSetupGuideCampaign.SET_UP_DEVICE_VISITOR_TRAFFIC_SOURCE_MARKET
          ] ?? false,
      },
      {
        id: EBaseSetupGuideCampaign.START_EXPERIMENT,
        title: t('Start experiment'),
        description: t(
          'Launch your A/B test and begin collecting data, you can pause or edit at any time.',
        ),
        completed: setupGuideStatuses?.[EBaseSetupGuideCampaign.START_EXPERIMENT] ?? false,
        isHiddenAction: isCampaignRunning,
        disabledReason: !isSelectedVariant
          ? t('Please set the Control & Variant URLs to proceed.')
          : undefined,
      },
    ];
  }, [t, setupGuideStatuses, isCampaignRunning, isSelectedControl, isSelectedVariant]);

  const handleUpdateSetupGuide = useCallback(
    async (setupGuideKeys: ISetupGuideCampaignUrlRedirect[]) => {
      if (!setupGuideStatuses) return;

      const newSetupGuideStatuses = mergeSetupGuideStatuses(setupGuideStatuses, setupGuideKeys);
      if (!newSetupGuideStatuses || shopMetaCreateOrUpdateMutation.isLoading) return;

      try {
        await shopMetaCreateOrUpdateMutation.mutateAsync({
          input: {
            key: GEMX_URL_REDIRECT_SETUP_GUIDE_KEY,
            value: JSON.stringify(newSetupGuideStatuses),
          },
        });

        queryClient.setQueryData(['ShopMetaByKey', { key: GEMX_URL_REDIRECT_SETUP_GUIDE_KEY }], {
          shopMetaByKey: { value: JSON.stringify(newSetupGuideStatuses) },
        });
      } catch (error) {
        captureSentryError(error, {
          source: 'useCampaignUrlRedirectSetupGuide.handleUpdateSetupGuide',
          tags: { experiment: 'Experiment' },
          level: 'warning',
        });
      }
    },
    [queryClient, shopMetaCreateOrUpdateMutation, setupGuideStatuses],
  );

  const handleShowSetupGuide = useCallback(
    (shouldCheck = false) => {
      if (!shouldCheck && (!windowWidth['1040Down'] || !expandedId)) return;

      setShowSetupGuide();
      setExpandedId(setupGuideSteps.find((item) => !item.completed)?.id);
    },
    [windowWidth, expandedId, setShowSetupGuide, setExpandedId, setupGuideSteps],
  );

  useEffectRunOnce(() => {
    setExpandedId(setupGuideSteps.find((item) => !item.completed)?.id);
  }, isFetchedSetupGuideMetaData && setupGuideState);

  const attemptedKeysRef = useRef<string>();

  useEffect(() => {
    if (!isFetchedSetupGuideMetaData || shopMetaCreateOrUpdateMutation.isLoading) return;

    const completedKeys = [
      isSelectedControl && ESetupGuideCampaignUrlRedirect.SET_CONTROL_URL,
      isSelectedVariant && ESetupGuideCampaignUrlRedirect.SET_VARIANT_URL,
    ].filter((key): key is ESetupGuideCampaignUrlRedirect => !!key);

    const attemptKey = completedKeys.join(',');
    // A failed write leaves the statuses unchanged, so retrying the same keys would loop.
    if (!completedKeys.length || attemptedKeysRef.current === attemptKey) return;

    attemptedKeysRef.current = attemptKey;
    handleUpdateSetupGuide(completedKeys);
  }, [
    handleUpdateSetupGuide,
    isFetchedSetupGuideMetaData,
    isSelectedControl,
    isSelectedVariant,
    shopMetaCreateOrUpdateMutation.isLoading,
  ]);

  return {
    setupGuideSteps,
    setupGuideState,
    handleUpdateSetupGuide,
    handleShowSetupGuide,
  };
};
